<template>
    <div ref="baggageComponent" :id="id">
        <BaggageSelectionForm
            :paxIndex="paxIndex"
            :onSelect="onSelect"
            :options="additionalBaggage?.Alternatives"
            :included="included?.baggages"
            :splitAlternatives="additionalBaggage?.SplitSector"
            :splitIncluded="included?.splitSector"
            :isOneWay="isOneWay"
            :smallBagIncluded="smallBagIncluded" />

        <WarningModal
            :visible="showWarning"
            :onSelect="(value) => (value ? acceptChange() : denyChange())">
            <BaggageWarning
                :baggage="upSellBaggage ? upSellBaggage : null"
                :isOneWay="isOneWay" />
        </WarningModal>
    </div>
</template>

<script lang="ts">
    import { PropType, computed, defineComponent, ref, watch } from 'vue';
    import { useStore } from '@/store';
    import { t } from '@shared/helpers/translation';
    import { isEmpty } from 'lodash';
    import { FlightProduct } from '@/api/Cart/cart.interfaces';
    import { Prompted } from '@/store/general/types';
    import { EventName } from '@/tracking/analytics/types';
    import { logAnalyticsAddRemoveBaggage } from '@/tracking/analytics/Checkout/Functions/checkout.functions';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { getBaggagesByTypeAndLeg } from '@shared/helpers/baggage';
    import {
        AdditionalBaggage,
        Baggage,
        BaggageIncluded,
        NO_SELECTION,
        UpSellBaggage,
    } from '@shared/interfaces/baggage';
    import {
        NoSelectionType,
        SelectionMetaData,
    } from '@ui/components/BaggageSelectionForm/BaggageSelectionForm.interfaces';
    import {
        logStatsigAddRemoveBaggage,
        logStatsigCheckoutBaggageWarningInteraction,
    } from '@/tracking/statsig/checkout.functions';
    import {
        BaggageWarningInteraction,
        BaggageWarningPosition,
        StatsigEvent,
    } from '@/tracking/statsig/types';
    import WarningModal from '@/components/shared/WarningModal.vue';
    import BaggageWarning from '@/components/shared/BaggageWarning/BaggageWarning.vue';
    import BaggageSelectionForm from '@ui/components/BaggageSelectionForm/BaggageSelectionForm.vue';

    export default defineComponent({
        name: 'Baggage',
        methods: { t, getMoneyObjectAsString },
        components: {
            BaggageWarning,
            BaggageSelectionForm,
            WarningModal,
        },
        props: {
            id: { type: String, required: true },
            additionalBaggage: {
                type: Object as PropType<AdditionalBaggage>,
                required: true,
            },
            included: {
                type: Object as PropType<{
                    splitSector: boolean;
                    baggages: BaggageIncluded[];
                }>,
                required: true,
            },
            paxIndex: { type: Number, required: true },
            product: {
                type: Object as () => FlightProduct,
                required: true,
            },
            smallBagIncluded: { type: Boolean, default: false },
        },

        setup(props) {
            const store = useStore();

            const showWarning = ref<boolean>(false);
            const selected = ref<Baggage[] | null>(null);
            const upSellBaggage = ref<UpSellBaggage | null>(null);

            const isOneWay = computed(() => props.product.Legs[1] === undefined);

            const prompted = computed((): Prompted => store.getters['general/getPrompted']);

            const setShowWarning = (visible: boolean) => {
                showWarning.value = visible;
            };

            const acceptChange = () => {
                setShowWarning(false);

                const baggage = upSellBaggage.value;

                if (baggage) {
                    const updatedAlternatives = [...props.additionalBaggage.Alternatives].map(
                        (x: Baggage) => {
                            return x.Id === baggage.Id ? { ...x, IsSelected: true } : x;
                        }
                    );

                    store.commit('cart/updateFlightPassengerBaggageState', {
                        product: props.product,
                        paxIndex: props.paxIndex,
                        alternatives: updatedAlternatives,
                    });
                }
                logStatsigCheckoutBaggageWarningInteraction(
                    BaggageWarningPosition.Selection,
                    BaggageWarningInteraction.Yes
                );
            };

            const denyChange = () => {
                setShowWarning(false);
                logStatsigCheckoutBaggageWarningInteraction(
                    BaggageWarningPosition.Selection,
                    BaggageWarningInteraction.No
                );
            };

            const setUpSellBaggage = (meta: SelectionMetaData) => {
                const baggages = getBaggagesByTypeAndLeg(
                    props.additionalBaggage,
                    meta.type,
                    meta.leg
                );

                if (baggages && !isEmpty(baggages)) {
                    upSellBaggage.value = { ...baggages[0], Leg: meta.leg };

                    setShowWarning(true);

                    store.commit('general/setPromptedBaggage', props.product.Id);
                } else {
                    upSellBaggage.value = null;
                }
            };

            const onSelect = (
                selection: Baggage | NoSelectionType,
                ids: string[],
                meta: SelectionMetaData
            ) => {
                const hasPromptedGlobal = prompted.value.baggageGlobal;

                const hasPrompted =
                    prompted.value.baggage.findIndex((x) => x === props.product.Id) !== -1;

                if (
                    selection === NO_SELECTION &&
                    props.additionalBaggage.ShowWarning &&
                    !hasPrompted &&
                    !hasPromptedGlobal
                ) {
                    setUpSellBaggage(meta);
                }

                const updatedAlternatives = [...props.additionalBaggage.Alternatives].map(
                    (bag: Baggage) => {
                        if (ids.includes(bag.Id)) {
                            handleTouchedField(bag.Id);

                            if (selection === NO_SELECTION) {
                                return { ...bag, IsSelected: false };
                            } else {
                                return {
                                    ...bag,
                                    IsSelected:
                                        selection.Type === bag.Type
                                            ? bag.Id === selection.Id
                                            : bag.IsSelected,
                                };
                            }
                        }

                        return bag;
                    }
                );

                selected.value = updatedAlternatives.filter((x) => x.IsSelected);

                store.commit('cart/updateFlightPassengerBaggageState', {
                    product: props.product,
                    paxIndex: props.paxIndex,
                    alternatives: updatedAlternatives,
                });
            };

            function handleTouchedField(name: string) {
                store.dispatch('checkout/setTouchedField', {
                    id: props.id,
                    name,
                });
            }

            watch(
                selected,
                (newBaggage: Baggage[] | null, oldBaggage: Baggage[] | null) => {
                    if (oldBaggage) {
                        oldBaggage.forEach((bag) => {
                            logAnalyticsAddRemoveBaggage(
                                bag,
                                props.product,
                                EventName.CHECKOUT_REMOVE_PRODUCT
                            );
                            logStatsigAddRemoveBaggage(
                                bag,
                                props.product,
                                StatsigEvent.CHECKOUT_REMOVE_BAGGAGE
                            );
                        });
                    }

                    if (newBaggage) {
                        newBaggage.forEach((bag) => {
                            logAnalyticsAddRemoveBaggage(
                                bag,
                                props.product,
                                EventName.CHECKOUT_ADD_PRODUCT
                            );
                            logStatsigAddRemoveBaggage(
                                bag,
                                props.product,
                                StatsigEvent.CHECKOUT_ADD_BAGGAGE
                            );
                        });
                    }
                },
                { deep: true }
            );

            return {
                isOneWay,
                acceptChange,
                denyChange,
                showWarning,
                upSellBaggage,
                onSelect,
            };
        },
    });
</script>
